import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  PdfSignOtpParams,
  PdfSignParams,
  PdfSignProvider,
  PdfSignResult,
  PdfSignStrategy,
  PdfSignUploadParams,
} from './pdf-sign.strategy';

/**
 * Proveedor "legacy": API vieja de documentos
 * (ServidorDocumentosLegacy -> TFMDocumentos/FrmPdfSign/...).
 * Replica las rutas del servicio interno de sf-pdfsign.
 */
@Injectable({ providedIn: 'root' })
export class LegacyPdfSignStrategy implements PdfSignStrategy {
  readonly provider: PdfSignProvider = 'legacy';

  private serverDocuments = '';

  constructor(private http: HttpClient) {}

  setEnviroments(enviroment: any): void {
    // Si falta ServidorDocumentosLegacy se cae a ServidorDocumentos; el aviso
    // lo emite DocumentService solo cuando un formulario elige este proveedor.
    this.serverDocuments =
      enviroment?.ServidorDocumentosLegacy ||
      enviroment?.ServidorDocumentos ||
      '';
  }

  private buildUrl(path: string): string {
    const base = (this.serverDocuments || '').replace(/\/+$/, '');
    const cleanPath = path.replace(/^\/+/, '');
    return `${base}/${cleanPath}`;
  }

  /**
   * El endpoint legacy recibe la ruta local del archivo codificada:
   * ":" -> "}", "\" y "/" -> "{", "." -> "(".
   */
  private encodeLegacyPath(path: string): string {
    return path
      .replace(/:/g, '}')
      .replace(/\\/g, '{')
      .replace(/\//g, '{')
      .replace(/\./g, '(');
  }

  getBlobDocument(path: string): Observable<Blob> {
    const encoded = this.encodeLegacyPath(path);
    return this.http
      .get(this.buildUrl(`Documentos/getFileXPath/${encoded}`), {
        responseType: 'blob',
      })
      .pipe(switchMap((blob) => this.rejectIfNotFound(blob)));
  }

  /**
   * El legacy responde 200 con el texto "file not found" cuando el archivo no
   * existe todavia. Se convierte en un error 404 para que DocumentService
   * reintente igual que con el proveedor platform.
   */
  private rejectIfNotFound(blob: Blob): Observable<Blob> {
    if (!blob || blob.size > 128) {
      return of(blob);
    }

    return from(blob.text()).pipe(
      map((text) => {
        if (/not\s*found/i.test(text)) {
          throw new HttpErrorResponse({
            status: 404,
            statusText: 'Not Found',
            error: text,
          });
        }
        return blob;
      }),
    );
  }

  getDococumentSign(idkatios: string, params: PdfSignParams): Observable<any> {
    const path = `FrmPdfSign/getDocSign/${idkatios}/${params.typeDocument}/${params.idDocument}/${params.tdoc}/${params.ndoc}`;
    return this.http.get<any>(this.buildUrl(path)).pipe(
      // El legacy devuelve el documento plano; si viniera envuelto en { data }
      // se toma data.
      map((response: any) =>
        response?.data?.Ruta !== undefined ? response.data : response,
      ),
    );
  }

  sendCodeOTP(idkatios: string, params: PdfSignOtpParams): Observable<any> {
    const data = {
      tipoAutenticacion: params.data.tipoAutenticacion,
      verficacion: params.data.verificacion,
    };
    const path = `FrmPdfSign/sendOTP/${idkatios}/${params.typeDocument}/${params.idDocument}/${params.tdoc}/${params.ndoc}`;
    return this.http.post(this.buildUrl(path), data);
  }

  verifyCodeOTP(
    idkatios: string,
    params: PdfSignOtpParams,
  ): Observable<PdfSignResult> {
    const path = `FrmPdfSign/valOTP/${idkatios}/${params.typeDocument}/${params.idDocument}/${params.tdoc}/${params.ndoc}`;
    return this.http
      .post<any>(this.buildUrl(path), params.data)
      .pipe(
        map((response: any) =>
          this.normalizeResult(response, 'No fue posible validar OTP'),
        ),
      );
  }

  uploadSignedDocument(
    idkatios: string,
    params: PdfSignUploadParams,
  ): Observable<PdfSignResult> {
    const path = `FrmPdfSign/uploadSign/${idkatios}/${params.typeDocument}/${params.idDocument}/${params.tdoc}/${params.ndoc}`;
    const formData = new FormData();
    formData.append('file', params.file, params.file.name);

    return this.http
      .post<any>(this.buildUrl(path), formData)
      .pipe(
        map((response: any) =>
          this.normalizeResult(response, 'No fue posible cargar el documento'),
        ),
      );
  }

  /** El legacy ya responde { Codigo, Descripcion, ... }; solo se protege el caso contrario. */
  private normalizeResult(
    response: any,
    fallbackMessage: string,
  ): PdfSignResult {
    if (response && typeof response === 'object' && 'Codigo' in response) {
      return response;
    }

    return {
      ...response,
      Codigo: String(response?.Codigo ?? response?.codigo ?? '-1'),
      Descripcion:
        response?.Descripcion || response?.descripcion || fallbackMessage,
    };
  }
}
